import { Archive, FileSpreadsheet, Printer, Scale } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  archiveComparison,
  downloadComparison,
  getClientComparison,
} from "@/features/clients/api";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import type { Client, ClientComparison, ComparisonWho } from "@/features/clients/types";
import { downloadBlob } from "@/lib/download";
import { formatCents } from "@/lib/utils";

const WHO_LABELS: Record<ComparisonWho, string> = {
  TAXPAYER: "Taxpayer",
  SPOUSE: "Spouse",
  TOTAL: "Total",
};

/**
 * Income Comparison report (legacy `comparison()`): Wage-&-Income (WAID)
 * income reported to the IRS vs the filed tax-return income, per category, per
 * tax year/filer. Exports the view as a PDF, an Excel workbook, or archives the
 * PDF into the client's Documents.
 */
export function ComparisonSection({ client }: { client: Client }) {
  const isMarried = client.maritalStatus === "MARRIED";
  // null = let the server pick the most recent year that actually has data; the
  // year options come from the server's availableYears, not a hardcoded range.
  const [year, setYear] = useState<number | null>(null);
  const [who, setWho] = useState<ComparisonWho>("TOTAL");
  const [data, setData] = useState<ClientComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"pdf" | "xlsx" | "archive" | null>(null);

  useEffect(() => {
    let active = true;
    // Loading is toggled inside the async callbacks (not synchronously in the
    // effect body) so a year/filer change refreshes without a spinner flash.
    getClientComparison(client.id, { year: year ?? undefined, who })
      .then((result) => active && setData(result))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [client.id, year, who]);

  const years = data?.availableYears ?? [];
  // The server echoes back the resolved year; show that while year is unset.
  const selectedYear = year ?? data?.taxYear ?? null;
  const rows = data?.rows ?? [];
  const hasRows = rows.length > 0;

  async function onExport(kind: "pdf" | "xlsx") {
    setBusy(kind);
    try {
      const blob = await downloadComparison(client.id, kind, {
        year: selectedYear ?? undefined,
        who,
      });
      downloadBlob(`income-comparison-${selectedYear ?? "latest"}.${kind}`, blob);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not export the comparison.");
    } finally {
      setBusy(null);
    }
  }

  async function onArchive() {
    setBusy("archive");
    try {
      await archiveComparison(client.id, { year: selectedYear ?? undefined, who });
      toast.success("Income comparison saved to Documents.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not archive the comparison.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 flex flex-wrap items-center justify-between gap-4 border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <Scale className="text-primary size-5" /> Income Comparison
        </h3>
        <div className="flex flex-wrap items-end gap-4">
          <div className="grid gap-2">
            <Label className={FIELD_LABEL}>Tax Year</Label>
            <Select
              value={selectedYear != null ? String(selectedYear) : ""}
              onValueChange={(v) => setYear(Number(v))}
              disabled={years.length === 0}
            >
              <SelectTrigger size="sm" className="w-32">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isMarried && (
            <div className="grid gap-2">
              <Label className={FIELD_LABEL}>Filer</Label>
              <Select value={who} onValueChange={(v) => setWho(v as ComparisonWho)}>
                <SelectTrigger size="sm" className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TOTAL">Total</SelectItem>
                  <SelectItem value="TAXPAYER">Taxpayer</SelectItem>
                  <SelectItem value="SPOUSE">Spouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Reported to IRS</TableHead>
                <TableHead className="text-right">Tax Return Filed</TableHead>
                <TableHead className="text-right">Difference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-muted-foreground py-12 text-center text-sm"
                  >
                    {years.length === 0
                      ? "No Wage-&-Income or tax-return transcript data on file for this client yet. Upload transcripts (Transcripts tab) to populate the comparison."
                      : `No ${WHO_LABELS[who]} income on file for ${selectedYear}. Pick another tax year above.`}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.category}>
                    <TableCell className="font-medium">{row.category}</TableCell>
                    <TableCell className="text-right">{formatCents(row.reportedToIrs)}</TableCell>
                    <TableCell className="text-right">{formatCents(row.taxReturnFiled)}</TableCell>
                    <TableCell className="text-right">{formatCents(row.difference)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex flex-wrap gap-3 border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasRows || busy !== null}
              onClick={() => void onExport("pdf")}
            >
              <Printer /> {busy === "pdf" ? "Generating…" : "Print PDF"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasRows || busy !== null}
              onClick={() => void onExport("xlsx")}
            >
              <FileSpreadsheet /> {busy === "xlsx" ? "Exporting…" : "Export Excel"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasRows || busy !== null}
              onClick={() => void onArchive()}
            >
              <Archive /> {busy === "archive" ? "Saving…" : "Archive PDF"}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
